import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { HIGHLIGHT_COLORS, highlightBg } from "@/lib/highlights";
import { ArrowLeft, Bookmark, ChevronLeft, ChevronRight, Check, Eraser, Headphones, Loader2, Pause, Play, Square, Star, X } from "lucide-react";

const LONG_PRESS_MS = 500;
const SPEEDS = [0.75, 1, 1.25, 1.5];

const TRANSLATIONS = [
  { k: "kjv", n: "KJV" },
  { k: "web", n: "WEB" },
];

function TranslationToggle({ translation, setTranslation }) {
  return (
    <div className="flex rounded-sm border border-border overflow-hidden text-xs">
      {TRANSLATIONS.map((t) => (
        <button key={t.k} data-testid={`reader-translation-${t.k}`} onClick={() => setTranslation(t.k)}
          className={`px-3 py-1.5 uppercase tracking-wider transition-colors ${translation === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>
          {t.n}
        </button>
      ))}
    </div>
  );
}

function ChapterFooterNav({ chapter, total, explored, setChapter, onFinish }) {
  return (
    <div className="flex items-center justify-between mt-14 pt-8 border-t border-border">
      <button data-testid="reader-prev-button" disabled={chapter <= 1} onClick={() => setChapter((c) => c - 1)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-0">
        <ChevronLeft className="w-4 h-4" /> Chapter {chapter - 1}
      </button>
      {chapter >= total ? (
        <button data-testid="reader-finish-button" onClick={onFinish}
          className={`flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm transition-colors ${explored ? "bg-muted text-foreground border border-border" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
          <Check className="w-4 h-4" /> {explored ? "Explored" : "Finish & mark explored"}
        </button>
      ) : (
        <button data-testid="reader-next-button" onClick={() => setChapter((c) => c + 1)}
          className="flex items-center gap-1.5 text-sm text-primary hover:text-accent transition-colors font-medium">
          Chapter {chapter + 1} <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function HighlightToolbar({ verse, currentColor, onPick, onClose, showActions, verseSaved, onSaveVerse, chapterBookmarked, onBookmarkChapter }) {
  return (
    <div className="fixed bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-30 bg-card border border-border rounded-md shadow-lg px-5 py-3.5 flex flex-col gap-3 animate-fade-up" data-testid="highlight-toolbar">
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground whitespace-nowrap">Verse {verse}</span>
        <div className="flex items-center gap-2.5">
          {HIGHLIGHT_COLORS.map((c) => (
            <button key={c.id} data-testid={`highlight-color-${c.id}`} onClick={() => onPick(c.id)}
              aria-label={`Highlight ${c.label}`} title={c.label}
              className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${currentColor === c.id ? "ring-2 ring-offset-2 ring-primary ring-offset-background" : ""}`}
              style={{ background: c.swatch }} />
          ))}
        </div>
        <button data-testid="highlight-remove" onClick={() => currentColor && onPick(currentColor)}
          disabled={!currentColor} aria-label="Remove highlight" title="Remove highlight"
          className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors disabled:opacity-30">
          <Eraser className="w-3.5 h-3.5" />
        </button>
        <button data-testid="highlight-close" onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>
      {showActions && (
        <div className="flex items-center gap-2 pt-3 border-t border-border" data-testid="verse-actions-row">
          <button data-testid="toolbar-save-verse" onClick={onSaveVerse}
            className={`flex items-center gap-2 text-sm rounded-sm px-4 py-2 transition-colors ${verseSaved ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted"}`}>
            <Star className={`w-4 h-4 ${verseSaved ? "fill-current" : ""}`} /> {verseSaved ? "Saved" : "Save verse"}
          </button>
          <button data-testid="toolbar-bookmark-chapter" onClick={onBookmarkChapter}
            className={`flex items-center gap-2 text-sm rounded-sm px-4 py-2 transition-colors ${chapterBookmarked ? "bg-primary text-primary-foreground" : "border border-border hover:bg-muted"}`}>
            <Bookmark className={`w-4 h-4 ${chapterBookmarked ? "fill-current" : ""}`} /> {chapterBookmarked ? "Bookmarked" : "Bookmark chapter"}
          </button>
        </div>
      )}
    </div>
  );
}

function AudioBar({ state, book, chapter, speed, onPlay, onPause, onStop, onSpeed, disabled }) {
  const label = {
    idle: "Listen to this chapter",
    loading: "Preparing audio…",
    playing: `Reading ${book} ${chapter}…`,
    paused: "Paused",
  }[state];
  return (
    <div className="flex items-center gap-3 bg-card border border-border rounded-md px-4 py-3 mb-6" data-testid="audio-player-bar">
      <Headphones className="w-4 h-4 text-accent shrink-0" />
      <button data-testid="audio-play-button" disabled={disabled || state === "loading"}
        onClick={state === "playing" ? onPause : onPlay}
        aria-label={state === "playing" ? "Pause" : "Read to me"}
        className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 shrink-0">
        {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" />
          : state === "playing" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <span className="text-sm text-muted-foreground flex-1 truncate" data-testid="audio-status-label">{label}</span>
      {(state === "playing" || state === "paused") && (
        <button data-testid="audio-stop-button" onClick={onStop} aria-label="Stop"
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0">
          <Square className="w-3 h-3" />
        </button>
      )}
      <button data-testid="audio-speed-button" onClick={onSpeed}
        className="text-xs border border-border rounded-sm px-2.5 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0">
        {speed}x
      </button>
    </div>
  );
}

export default function BibleReader() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chapter, setChapter] = useState(parseInt(searchParams.get("chapter") || "1", 10));
  const [translation, setTranslation] = useState("kjv");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [explored, setExplored] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [highlights, setHighlights] = useState({});
  const [activeVerse, setActiveVerse] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [savedRefs, setSavedRefs] = useState([]);
  const reqId = useRef(0);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);
  const [audioState, setAudioState] = useState("idle");
  const [speed, setSpeed] = useState(1);
  const [playingVerse, setPlayingVerse] = useState(null);
  const audioRef = useRef(null);
  const autoNext = useRef(false);

  useEffect(() => {
    api.get("/bible/read").then((r) => setExplored(r.data.includes(slug)));
    api.get("/bible/bookmarks").then((r) => setBookmarks(r.data));
    api.get("/verses/saved").then((r) => setSavedRefs(r.data.map((v) => v.ref)));
  }, [slug]);

  useEffect(() => {
    setActiveVerse(null);
    api.post("/bible/position", { slug, chapter }).catch(() => {});
    api.get("/bible/highlights", { params: { slug, chapter } })
      .then((r) => setHighlights(Object.fromEntries(r.data.map((h) => [h.verse, h.color]))));
  }, [slug, chapter]);

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    setSearchParams({ chapter: String(chapter) }, { replace: true });
    api.get(`/bible/books/${slug}/chapter/${chapter}`, { params: { translation } })
      .then((r) => { if (id === reqId.current) setData(r.data); })
      .catch(() => { if (id === reqId.current) toast.error("Could not load this chapter. Please try again."); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
    window.scrollTo({ top: 0 });
  }, [slug, chapter, translation, setSearchParams]);

  const total = data?.chapters || 1;
  const isBookmarked = bookmarks.some((b) => b.slug === slug && b.chapter === chapter);

  const markExplored = async () => {
    if (explored) return;
    try {
      await api.post(`/bible/books/${slug}/read`);
      setExplored(true);
      toast.success(`${data?.book} marked as explored.`);
    } catch { toast.error("Could not update."); }
  };

  const toggleBookmark = async () => {
    try {
      const { data: res } = await api.post("/bible/bookmarks", { slug, chapter });
      setBookmarks((bs) => res.bookmarked
        ? [...bs, { slug, chapter, book: data?.book }]
        : bs.filter((b) => !(b.slug === slug && b.chapter === chapter)));
      toast.success(res.bookmarked ? "Chapter bookmarked." : "Bookmark removed.");
    } catch { toast.error("Could not update bookmark."); }
  };

  const applyHighlight = async (color) => {
    const v = data?.verses.find((x) => x.verse === activeVerse);
    try {
      const { data: res } = await api.post("/bible/highlights",
        { slug, chapter, verse: activeVerse, color, text: v?.text || "" });
      setHighlights((h) => {
        const copy = { ...h };
        if (res.highlighted) copy[activeVerse] = res.color;
        else delete copy[activeVerse];
        return copy;
      });
      setActiveVerse(null);
    } catch { toast.error("Could not save highlight."); }
  };

  const verseRef = activeVerse != null && data ? `${data.book} ${chapter}:${activeVerse}` : null;
  const verseSaved = verseRef != null && savedRefs.includes(verseRef);

  const saveActiveVerse = async () => {
    const v = data?.verses.find((x) => x.verse === activeVerse);
    if (!v || !verseRef) return;
    try {
      const { data: res } = await api.post("/verses/save",
        { ref: verseRef, kjv: v.text, niv: v.text, theme: data.book });
      setSavedRefs((rs) => (res.saved ? [...rs, verseRef] : rs.filter((r) => r !== verseRef)));
      toast.success(res.saved ? "Verse saved to your collection." : "Verse removed from your collection.");
    } catch { toast.error("Could not save verse."); }
  };

  const openToolbar = (verse, withActions) => {
    setShowActions(withActions);
    setActiveVerse((av) => (av === verse && !withActions ? null : verse));
  };

  const pressStart = (verse) => {
    longPressed.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      openToolbar(verse, true);
    }, LONG_PRESS_MS);
  };

  const pressEnd = (verse) => {
    clearTimeout(pressTimer.current);
    if (!longPressed.current) openToolbar(verse, false);
  };

  const pressCancel = () => clearTimeout(pressTimer.current);

  const stopAudio = () => {
    const el = audioRef.current;
    if (el) {
      el.onplay = el.onpause = el.onended = el.ontimeupdate = null;
      el.pause();
      URL.revokeObjectURL(el.src);
      audioRef.current = null;
    }
    setPlayingVerse(null);
    setAudioState("idle");
  };

  const startAudio = async () => {
    if (audioRef.current && audioState === "paused") {
      audioRef.current.play();
      return;
    }
    if (!data) return;
    stopAudio();
    setAudioState("loading");
    try {
      const res = await fetch(`${API}/bible/audio/${slug}/${chapter}?translation=${translation}`, { credentials: "include" });
      if (!res.ok) throw new Error("audio failed");
      const blob = await res.blob();
      const el = new Audio(URL.createObjectURL(blob));
      el.playbackRate = speed;
      const introLen = data.book.length + 12;
      const lens = data.verses.map((v) => v.text.length + 1);
      const totalLen = introLen + lens.reduce((a, b) => a + b, 0);
      let acc = introLen;
      const fractions = data.verses.map((v, i) => {
        acc += lens[i];
        return { verse: v.verse, end: acc / totalLen };
      });
      el.ontimeupdate = () => {
        if (!el.duration) return;
        const frac = el.currentTime / el.duration;
        const cur = fractions.find((f) => frac <= f.end);
        setPlayingVerse(cur ? cur.verse : null);
      };
      el.onplay = () => setAudioState("playing");
      el.onpause = () => { if (!el.ended) setAudioState("paused"); };
      el.onended = () => {
        setPlayingVerse(null);
        if (chapter < total) {
          autoNext.current = true;
          setChapter((c) => c + 1);
        } else {
          stopAudio();
          toast.success(`End of ${data.book}. Amen.`);
        }
      };
      audioRef.current = el;
      await el.play();
    } catch {
      setAudioState("idle");
      toast.error("Could not load audio. Please try again.");
    }
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  useEffect(() => stopAudio, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoNext.current) stopAudio();
  }, [slug, chapter, translation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (data && autoNext.current) {
      autoNext.current = false;
      startAudio();
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (playingVerse == null) return;
    document.querySelector(`[data-testid="verse-${playingVerse}"]`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [playingVerse]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="flex items-center justify-between mb-10">
        <button data-testid="reader-back-button" onClick={() => navigate(`/scripture/${slug}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Overview
        </button>
        <div className="flex items-center gap-3">
          <TranslationToggle translation={translation} setTranslation={setTranslation} />
          <button data-testid="reader-bookmark-button" onClick={toggleBookmark}
            aria-label={isBookmarked ? "Remove bookmark" : "Bookmark this chapter"}
            className="text-muted-foreground hover:text-accent transition-colors">
            <Bookmark className={`w-5 h-5 ${isBookmarked ? "fill-accent text-accent" : ""}`} />
          </button>
        </div>
      </div>

      <div className="flex items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <p className="label-overline mb-1">{translation.toUpperCase()}</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none" data-testid="reader-title">
            {data?.book || "…"} {chapter}
          </h1>
        </div>
        <select data-testid="reader-chapter-select" value={chapter} onChange={(e) => setChapter(parseInt(e.target.value, 10))}
          className="bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-primary">
          {Array.from({ length: total }, (_, i) => i + 1).map((c) => (
            <option key={c} value={c}>Chapter {c}</option>
          ))}
        </select>
      </div>

      <AudioBar state={audioState} book={data?.book} chapter={chapter} speed={speed}
        onPlay={startAudio} onPause={() => audioRef.current?.pause()} onStop={stopAudio}
        onSpeed={cycleSpeed} disabled={loading || !data} />

      <p className="text-xs text-muted-foreground mb-6">Tap a verse to highlight it · press & hold to save or bookmark.</p>

      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
        </div>
      ) : (
        <div className="font-scripture text-lg sm:text-xl leading-loose text-foreground animate-fade-up" data-testid="reader-text">
          {data?.verses.map((v) => (
            <span key={v.verse} data-testid={`verse-${v.verse}`} role="button" tabIndex={0}
              onPointerDown={() => pressStart(v.verse)}
              onPointerUp={() => pressEnd(v.verse)}
              onPointerLeave={pressCancel}
              onPointerCancel={pressCancel}
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={(e) => { if (e.key === "Enter") openToolbar(v.verse, true); }}
              className={`cursor-pointer select-none rounded-sm transition-colors box-decoration-clone px-0.5 ${activeVerse === v.verse ? "outline outline-1 outline-accent/60" : ""} ${playingVerse === v.verse ? "outline outline-2 outline-accent/70" : ""}`}
              style={highlights[v.verse] ? { backgroundColor: highlightBg(highlights[v.verse]) } : undefined}>
              <sup className="font-body text-[0.62em] text-accent font-semibold mr-1 align-super">{v.verse}</sup>
              {v.text}{" "}
            </span>
          ))}
        </div>
      )}

      {/* Chapter navigation */}
      <ChapterFooterNav chapter={chapter} total={total} explored={explored} setChapter={setChapter} onFinish={markExplored} />

      {activeVerse != null && (
        <HighlightToolbar verse={activeVerse} currentColor={highlights[activeVerse]}
          onPick={applyHighlight} onClose={() => setActiveVerse(null)}
          showActions={showActions} verseSaved={verseSaved} onSaveVerse={saveActiveVerse}
          chapterBookmarked={isBookmarked} onBookmarkChapter={toggleBookmark} />
      )}
    </div>
  );
}
